import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Loader } from 'lucide-react';

const BANNER_ROUTES = {
  '/': 'home',
  '/learn': 'learn',
  '/news': 'news',
  '/about': 'about',
  '/support': 'support',
};

export function NewsletterSignupBanner() {
  const { pathname } = useLocation();
  const source = BANNER_ROUTES[pathname];
  if (!source) return null;
  return <NewsletterSignupSection source={source} />;
}

const NewsletterSignupSection = ({ source = 'newsletter_page' }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(null);
  const [formError, setFormError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    setSubmitting(true);

    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          source,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to subscribe. Please try again.');
      setFormSuccess(data.message || 'Thank you for subscribing! Check your inbox to confirm your subscription.');
      setEmail('');
    } catch (err) {
      setFormError(err.message || 'Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="newsletter-signup-section" aria-labelledby="newsletter-signup-heading">
      <div className="newsletter-signup-inner">
        <h2 id="newsletter-signup-heading">Stay Ahead with Weekly Market Insights</h2>
        <p>
          Curated sector trends, research highlights, and product updates — delivered once a week.
          No spam, unsubscribe anytime.
        </p>

        {formSuccess && (
          <div className="newsletter-signup-banner newsletter-signup-banner--success" role="status">
            {formSuccess}
          </div>
        )}
        {formError && (
          <div className="newsletter-signup-banner newsletter-signup-banner--error" role="alert">
            {formError}
          </div>
        )}

        {!formSuccess && (
          <form className="newsletter-signup-form" onSubmit={handleSubmit} noValidate>
            <input
              type="email"
              className="newsletter-signup-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              aria-label="Email address"
            />
            <button
              type="submit"
              className="hp-btn hp-btn-primary newsletter-signup-submit"
              disabled={submitting}
            >
              {submitting ? <Loader size={16} className="spinning" aria-hidden /> : <Mail size={16} aria-hidden />}
              {submitting ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
        )}

        <p className="newsletter-signup-footnote">
          We respect your privacy. Your email is used only for newsletter delivery.{' '}
          <Link to="/newsletter">Learn more</Link>
        </p>
      </div>
    </section>
  );
};

export default NewsletterSignupSection;
